// components/VATRecordsList.js
import React, { useState, useEffect, useCallback } from "react";
import vatAPI from "../api/vatAPI";
import formAPI from "../api/formAPI";
import { formatCurrency } from "../utils/numberUtils";
import { formatDate } from "../utils/dateUtils";
import ChartOfAccountsSearch from "./ChartOfAccountsSearch";
import Button from "./Button";
import MoneyInput from "./MoneyInput";
import ConfirmModal from "./ConfirmModal";
import notify from "../lib/notify";
import { cleanTIN, findMissingTINs, missingTINMessage } from "../utils/tinFormat";
import { resolveFtypeCode, missingFtypeMessage } from "../utils/sawtFormType";
import { filerRdoCode, missingRdoMessage } from "../utils/rdoCode";
import { isVatWithholdingAtc, findMissingATCs, missingATCMessage } from "../utils/withholdingAtc";
import { buildSawtDat, downloadSawtDat } from "../utils/sawtDat";
import { datFilerBranch } from '../utils/branchCode';
import { recordAttachmentExport as markAttachmentExported, ATTACHMENT_TYPES } from "../utils/recordAttachmentExport";
import { Download, Pencil, Trash2 } from "lucide-react";
import { useVendorTerms } from '../utils/terminology';

/**
 * Supports:
 * - VAT records (editable)
 * - Form 2306 records (editable: AMOUNT only)
 */

const VATRecordsList = ({
  records,
  onRecordDeleted,
  onRecordUpdated,
  showFilters = false,
  showActions = true,
  showQuarterFilter = false,
  client = null,
}) => {
  const term = useVendorTerms();
  const QUARTER_MONTHS = {
    1: [{ value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' }],
    2: [{ value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' }],
    3: [{ value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' }],
    4: [{ value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }],
  };

  const [filter, setFilter] = useState("all"); // all | input | output | form2306
  const [quarterFilter, setQuarterFilter] = useState("all");
  const [yearFilter, setYearFilter] = useState("all");
  const [deletingId, setDeletingId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [availableQuarters, setAvailableQuarters] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [exportingDat, setExportingDat] = useState(false);
  const [showExportDatDialog, setShowExportDatDialog] = useState(false);
  const [showValidationGuide, setShowValidationGuide] = useState(false);
  const [hideValidationGuide, setHideValidationGuide] = useState(
    () => localStorage.getItem('hideReliefValidationGuide') === 'true'
  );
  const [exportQuarter, setExportQuarter] = useState(null);
  const [exportYear, setExportYear] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [periodFilter, setPeriodFilter] = useState("all"); // 'all' or 'Q-Y'
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState("");
  const [auditRecord, setAuditRecord] = useState(null);
  const [auditData, setAuditData] = useState(null);
  const [monthFilter, setMonthFilter] = useState("all");

  // Column resizing state
  const DEFAULT_COL_WIDTHS = {
    type: 120,
    period: 150,
    description: 300,
    date: 130,
    gross: 140,
    tax: 140,
    net: 140,
    actions: 130,
  };
  const [colWidths, setColWidths] = useState({ ...DEFAULT_COL_WIDTHS });
  const [resizing, setResizing] = useState(null); // { col: string, startX: number, startWidth: number }

  // Resize handlers
  const handleResizeStart = useCallback((e, col) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX || e.touches?.[0]?.clientX || 0;
    const startWidth = colWidths[col];
    setResizing({ col, startX, startWidth });
  }, [colWidths]);

  // Mouse/touch move and up handlers
  useEffect(() => {
    if (!resizing) return;

    const handleMouseMove = (e) => {
      const currentX = e.clientX || e.touches?.[0]?.clientX || 0;
      const diff = currentX - resizing.startX;
      const newWidth = Math.max(80, resizing.startWidth + diff);
      setColWidths((prev) => ({ ...prev, [resizing.col]: newWidth }));
    };

    const handleMouseUp = () => {
      setResizing(null);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("touchmove", handleMouseMove);
    document.addEventListener("touchend", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", handleMouseMove);
      document.removeEventListener("touchend", handleMouseUp);
    };
  }, [resizing]);

  // Prevent text selection during resize
  useEffect(() => {
    if (resizing) {
      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";
    } else {
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    }
    return () => {
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, [resizing]);

  // ResizableHeader component
  const ResizableHeader = useCallback(
    ({ col, label, align = "left", className = "", isSticky = false }) => (
      <th
        className={`px-6 py-3 text-${align} text-xs font-medium text-gray-500 uppercase relative select-none ${
          isSticky ? "sticky right-0 bg-gray-50 z-10 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.08)]" : ""
        } ${className}`}
        style={{ width: colWidths[col], minWidth: 80 }}
      >
        {label}
        <div
          className="absolute top-0 right-0 w-2 h-full cursor-col-resize hover:bg-teal-300/50 active:bg-teal-400/50 z-20"
          onMouseDown={(e) => handleResizeStart(e, col)}
          onTouchStart={(e) => handleResizeStart(e, col)}
        />
      </th>
    ),
    [colWidths, handleResizeStart]
  );

  const quarterFromMonth = useCallback((month) => {
    const m = Number(month) || 1;
    if (m <= 3) return 1;
    if (m <= 6) return 2;
    if (m <= 9) return 3;
    return 4;
  }, []);

  const MONTH_OPTIONS = [
    { value: 1, label: "Jan" }, { value: 2, label: "Feb" }, { value: 3, label: "Mar" },
    { value: 4, label: "Apr" }, { value: 5, label: "May" }, { value: 6, label: "Jun" },
    { value: 7, label: "Jul" }, { value: 8, label: "Aug" }, { value: 9, label: "Sep" },
    { value: 10, label: "Oct" }, { value: 11, label: "Nov" }, { value: 12, label: "Dec" },
  ];

  // Category facts, not statuses — uniform tint per DESIGN.md §5.1; text differentiates, not hue.
  const PURCHASE_TYPE_LABELS = {
    goods_other: { label: "Goods Other", color: "bg-ink-canvas text-ink-body" },
    capital_goods: { label: "Capital Goods", color: "bg-ink-canvas text-ink-body" },
    services: { label: "Services", color: "bg-ink-canvas text-ink-body" },
  };

  const getRecordKind = useCallback((record) => {
    if (!record) return "vat";

    // 2307 must be tested BEFORE the 2306 heuristics below. An auto-generated
    // 2307 carries a vendor plus baseAmount/taxWithheld/withholdingRate, which
    // is exactly the shape those heuristics read as a 2306 — so without this it
    // would be mislabelled. VATManagement tags them explicitly rather than
    // leaving it to inference.
    if (record?.formType === "2307" || record?.form === "2307" || record?.docType === "2307") return "form2307";

    if (record?.formType === "2306" || record?.form === "2306" || record?.docType === "2306") return "form2306";

    const hasVendor = typeof record?.vendor === "object" && (record.vendor?.name || record.vendor?._id);
    const hasBaseTax = record?.baseAmount != null || record?.taxWithheld != null || record?.withholdingRate != null;
    if (hasVendor && hasBaseTax) return "form2306";

    const hasPayee = typeof record?.payeeName === "string" && record.payeeName.trim().length > 0;
    const hasWithheld = record?.withheldAmount != null || record?.withheldRate != null || record?.netAmount != null;
    if (hasPayee && hasWithheld) return "form2306";

    return "vat";
  }, []);

  // 2306 and 2307 are both withholding certificates and carry the same
  // vendor/baseAmount/taxWithheld shape, so every display path treats them
  // alike; only the label differs. Kept as a predicate so a future third
  // certificate does not need another sweep through these branches.
  const isCertificateKind = useCallback((kind) => kind === "form2306" || kind === "form2307", []);

  const getVatRecordType = useCallback((record) => record?.recordType || "", []);

  const isEditableVatRecord = useCallback(
    (record) => showActions && getRecordKind(record) === "vat",
    [getRecordKind, showActions]
  );

  const isEditableForm2306Record = useCallback(
    (record) => showActions && getRecordKind(record) === "form2306",
    [getRecordKind, showActions]
  );

  const getPeriod = useCallback(
    (record) => {
      const fiscalYear = Number(record?.fiscalYear) || new Date().getFullYear();
      let quarter = Number(record?.quarter);

      if (!quarter || isNaN(quarter)) {
        const month = Number(record?.month);
        if (month && !isNaN(month)) quarter = quarterFromMonth(month);
        else quarter = 1;
      }

      return { quarter, fiscalYear };
    },
    [quarterFromMonth]
  );

  useEffect(() => {
    const years = new Set();
    const quarters = new Set();

    (records || []).forEach((record) => {
      const { quarter, fiscalYear } = getPeriod(record);
      if (fiscalYear) years.add(fiscalYear);
      if (quarter) quarters.add(quarter);
    });

    setAvailableYears(Array.from(years).sort((a, b) => b - a));
    setAvailableQuarters(Array.from(quarters).sort((a, b) => a - b));
  }, [records, getPeriod]);

  const getUniquePeriods = useCallback(() => {
    const periodsMap = new Map();

    (records || []).forEach((record) => {
      const { quarter, fiscalYear } = getPeriod(record);
      if (quarter && fiscalYear) {
        const key = `${quarter}-${fiscalYear}`;
        const prev = periodsMap.get(key);
        if (!prev) periodsMap.set(key, { quarter, year: fiscalYear, label: `Q${quarter} ${fiscalYear}`, count: 1 });
        else periodsMap.set(key, { ...prev, count: prev.count + 1 });
      }
    });

    return Array.from(periodsMap.values()).sort((a, b) => (a.year !== b.year ? b.year - a.year : b.quarter - a.quarter));
  }, [records, getPeriod]);

  const uniquePeriods = getUniquePeriods();

  const filteredRecords = useCallback(() => {
    return (records || []).filter((record) => {
      const kind = getRecordKind(record);
      const vatType = getVatRecordType(record);
      const { quarter, fiscalYear } = getPeriod(record);

      if (filter !== "all") {
        if (filter === "form2306" && kind !== "form2306") return false;
        if (filter === "form2307" && kind !== "form2307") return false;
        if ((filter === "input" || filter === "output") && !(kind === "vat" && vatType === filter)) return false;
      }

      if (quarterFilter !== "all" && String(quarter) !== String(quarterFilter)) return false;
      if (yearFilter !== "all" && String(fiscalYear) !== String(yearFilter)) return false;

      if (periodFilter !== "all") {
        const [q, y] = periodFilter.split("-");
        if (quarter !== parseInt(q, 10) || fiscalYear !== parseInt(y, 10)) return false;
      }

      if (selectedMonth !== null && Number(record?.month) !== selectedMonth) return false;

      if (monthFilter !== "all") {
        const recordMonth = Number(record?.month);
        if (recordMonth !== parseInt(monthFilter, 10)) return false;
      }

      return true;
    });
  }, [records, filter, quarterFilter, yearFilter, periodFilter, selectedMonth, monthFilter, getRecordKind, getVatRecordType, getPeriod]);

  const getQuarterDisplay = useCallback(
    (record) => {
      const { quarter, fiscalYear } = getPeriod(record);
      const quarterNames = {
        1: `Q1 (Jan - Mar ${fiscalYear})`,
        2: `Q2 (Apr - Jun ${fiscalYear})`,
        3: `Q3 (Jul - Sep ${fiscalYear})`,
        4: `Q4 (Oct - Dec ${fiscalYear})`,
      };

      return {
        quarter,
        fiscalYear,
        label: quarterNames[quarter] || `Q${quarter} ${fiscalYear}`,
        fullLabel: `Q${quarter} ${fiscalYear}`,
      };
    },
    [getPeriod]
  );

  const isVatExempt = useCallback(
    (record) => {
      if (getRecordKind(record) !== "vat") return false;
      return (
        record.isVatExempt === true ||
        (typeof record.vatExemptAmount === "number" && !isNaN(record.vatExemptAmount) && record.vatExemptAmount > 0) ||
        (typeof record.vatExempt === "number" && !isNaN(record.vatExempt) && record.vatExempt > 0)
      );
    },
    [getRecordKind]
  );

  const getForm2306Payee = useCallback((record) => {
    if (record?.payeeName) return { name: record.payeeName, tin: record.payeeTin || "" };
    if (typeof record?.vendor === "object" && record.vendor) {
      return { name: record.vendor?.name || "—", tin: record.vendor?.tin || record.vendor?.code || "" };
    }
    return { name: "—", tin: "" };
  }, []);

  const getDisplayValues = useCallback(
    (record) => {
      const kind = getRecordKind(record);

      if (kind === "form2306" || kind === "form2307") {
        const gross = Number(record?.baseAmount ?? record?.grossAmount ?? 0) || 0;
        const tax = Number(record?.taxWithheld ?? record?.withheldAmount ?? 0) || 0;
        const net = Number(record?.netAmount ?? (gross - tax)) || 0;
        return { grossAmount: gross, taxAmount: tax, netAmount: net };
      }

      if (isVatExempt(record)) {
        const exemptAmount = record.vatExemptAmount || record.vatExempt || record.grossAmount || 0;
        return { grossAmount: Number(exemptAmount) || 0, taxAmount: 0, netAmount: Number(exemptAmount) || 0 };
      }

      return {
        grossAmount: Number(record.grossAmount) || 0,
        taxAmount: Number(record.vatAmount) || 0,
        netAmount: Number(record.netAmount) || 0,
      };
    },
    [getRecordKind, isVatExempt]
  );

  const padTIN = useCallback((tin) => {
    const digits = String(tin || "").replace(/\D/g, "");
    if (digits.length === 0) return "000000000";
    return digits.padStart(9, "0").slice(0, 9);
  }, []);

  const quoteString = useCallback((value) => {
    const text = String(value || "").replace(/"/g, '""');
    return `"${text}"`;
  }, []);

  const formatTaxableMonth = useCallback((record) => {
    const month = Number(record?.month);
    const year = Number(record?.fiscalYear);
    if (!month || isNaN(month) || !year || isNaN(year)) return "";
    const lastDay = new Date(year, month, 0);
    const mm = String(lastDay.getMonth() + 1).padStart(2, "0");
    const dd = String(lastDay.getDate()).padStart(2, "0");
    return `${mm}/${dd}/${lastDay.getFullYear()}`;
  }, []);

  const downloadCSVFile = useCallback((content, filename) => {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  // Helper: format last day of month for SLSP/SLSS (M/D/YYYY)
  const getLastDayOfMonth = useCallback((month, fiscalYear) => {
    const d = new Date(fiscalYear, month, 0);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }, []);

  // Helper: format number to Philippine locale with commas (##,##0.00)
  const fmt = useCallback((n) => {
    const num = Number(n || 0);
    return num.toLocaleString('en-PH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, []);

  // Helper: format number and wrap in quotes for CSV (because of commas in value)
  const numCell = useCallback((n) => {
    const text = fmt(n).replace(/"/g, '""');
    return `"${text}"`;
  }, [fmt]);

  // Helper: resolve address from string or object
  const resolveAddress = useCallback((addr) => {
    if (!addr) return '';
    if (typeof addr === 'string') return String(addr).toUpperCase();
    // If address is an object, concatenate known fields
    const parts = [
      addr.street || addr.line1 || addr.addressLine1 || '',
      addr.barangay || addr.brgy || '',
      addr.city || addr.municipality || '',
      addr.province || addr.state || '',
      addr.zipCode || addr.zip || '',
    ].filter(Boolean);
    return parts.join(', ').toUpperCase();
  }, []);

  // Helper: escape CSV cell with quotes
  const cell = useCallback((v) => {
    const text = String(v ?? '').replace(/"/g, '""');
    return `"${text}"`;
  }, []);

  // Filing-card attachment tracking only makes sense when the export is
  // scoped to one specific quarter — "All Periods" exports span multiple
  // periods and can't be attributed to a single filing record.
  const recordAttachmentExport = useCallback((type, total) => {
    if (!client?._id || periodFilter === 'all') return;
    const [q, y] = periodFilter.split('-');
    return markAttachmentExported({
      clientId: client._id,
      formCode: '2550Q',
      fiscalYear: parseInt(y, 10),
      quarter: parseInt(q, 10),
      type,
      total,
    });
  }, [client?._id, periodFilter]);

  const exportSLSP = useCallback(() => {
    // Call filteredRecords() fresh to get current filtered view
    const filtered = filteredRecords();
    const inputRecords = filtered
      .filter(r => getRecordKind(r) === 'vat' && r.recordType === 'input')
      .sort((a, b) => {
        if (a.fiscalYear !== b.fiscalYear) return a.fiscalYear - b.fiscalYear;
        return (a.month || 0) - (b.month || 0);
      });

    if (inputRecords.length === 0) {
      alert('No input VAT records found for the selected filter.');
      return;
    }

    // Build totals for GRAND TOTAL row
    const totals = { 6: 0, 7: 0, 8: 0, 9: 0, 10: 0, 11: 0, 12: 0, 13: 0, 14: 0 };

    const dataRows = inputRecords.map((r) => {
      const gross = Number(r.grossAmount || 0);
      const exempt = r.isVatExempt ? gross : 0;
      const zeroRated = r.isZeroRated ? gross : 0;
      const taxable = (!r.isVatExempt && !r.isZeroRated) ? Number(r.netAmount || 0) : 0;
      const services = (r.purchaseType === 'services' && !r.isVatExempt && !r.isZeroRated) ? gross : 0;
      const capital = (r.purchaseType === 'capital_goods' && !r.isVatExempt && !r.isZeroRated) ? gross : 0;
      const goodsOther = ((r.purchaseType === 'goods_other' || !r.purchaseType) && !r.isVatExempt && !r.isZeroRated) ? gross : 0;
      const inputTax = Number(r.vatAmount || 0);
      const grossTaxable = (!r.isVatExempt && !r.isZeroRated) ? gross : 0;

      // Determine registered name vs supplier name based on classification
      const registeredName = r.vendorClassification === 'corporation'
        ? String(r.vendorName || '').toUpperCase()
        : '';
      const supplierName = (r.vendorClassification === 'individual' || !r.vendorClassification)
        ? String(r.vendorName || '').toUpperCase()
        : '';

      totals[6] += gross;
      totals[7] += exempt;
      totals[8] += zeroRated;
      totals[9] += taxable;
      totals[10] += services;
      totals[11] += capital;
      totals[12] += goodsOther;
      totals[13] += inputTax;
      totals[14] += grossTaxable;

      return [
        cell(getLastDayOfMonth(r.month, r.fiscalYear)),
        cell(r.vendorTin || ''),
        cell(registeredName),
        cell(supplierName),
        cell(resolveAddress(r.supplierAddress)),
        numCell(gross),
        numCell(exempt),
        numCell(zeroRated),
        numCell(taxable),
        numCell(services),
        numCell(capital),
        numCell(goodsOther),
        numCell(inputTax),
        numCell(grossTaxable),
      ].join(',');
    });

    const clientName = String(client?.name || '').toUpperCase();
    const clientTin = client?.tin || '';
    const clientTradeName = String(client?.tradeName || client?.name || '').toUpperCase();
    const clientAddress = resolveAddress(
      client?.address ||
      client?.businessAddress ||
      client?.contactInfo ||
      ''
    );

    const rows = [
      'PURCHASE TRANSACTION',
      'RECONCILIATION OF LISTING FOR ENFORCEMENT',
      '',
      `TIN: ${clientTin}`,
      `OWNER'S NAME: ${clientName}`,
      `OWNER'S TRADE NAME: ${clientTradeName}`,
      `OWNER'S ADDRESS: ${clientAddress}`,
      '',
      [
        cell('TAXABLE MONTH'),
        cell('TAXPAYER IDENTIFICATION NUMBER'),
        cell('REGISTERED NAME'),
        cell('NAME OF SUPPLIER (LAST NAME, FIRST NAME, MIDDLE NAME)'),
        cell('SUPPLIER\'S ADDRESS'),
        cell('AMOUNT OF GROSS PURCHASE'),
        cell('AMOUNT OF EXEMPT PURCHASE'),
        cell('AMOUNT OF ZERO-RATED PURCHASE'),
        cell('AMOUNT OF TAXABLE PURCHASE'),
        cell('AMOUNT OF PURCHASE OF SERVICES'),
        cell('AMOUNT OF PURCHASE OF CAPITAL GOODS'),
        cell('AMOUNT OF PURCHASE OF GOODS OTHER THAN CAPITAL GOODS'),
        cell('INPUT TAX'),
        cell('AMOUNT OF GROSS TAXABLE PURCHASE'),
      ].join(','),
      '(1),(2),(3),(4),(5),(6),(7),(8),(9),(10),(11),(12),(13),(14)',
      ...dataRows,
      [
        cell('GRAND TOTAL'),
        '',
        '',
        '',
        '',
        numCell(totals[6]),
        numCell(totals[7]),
        numCell(totals[8]),
        numCell(totals[9]),
        numCell(totals[10]),
        numCell(totals[11]),
        numCell(totals[12]),
        numCell(totals[13]),
        numCell(totals[14]),
      ].join(','),
      'END OF REPORT',
    ];

    const csv = rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `SLSP_${clientName}_${Date.now()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    recordAttachmentExport(ATTACHMENT_TYPES.SLSP_INPUT, totals[13]);
  }, [client?.name, client?.tin, client?.tradeName, client?.address, client?.businessAddress, client?.contactInfo, filteredRecords, getRecordKind, getLastDayOfMonth, fmt, numCell, resolveAddress, cell, recordAttachmentExport]);

  const exportSLSS = useCallback(() => {
    // Call filteredRecords() fresh to get current filtered view
    const filtered = filteredRecords();
    const outputRecords = filtered
      .filter(r => getRecordKind(r) === 'vat' && r.recordType === 'output')
      .sort((a, b) => {
        if (a.fiscalYear !== b.fiscalYear) return a.fiscalYear - b.fiscalYear;
        return (a.month || 0) - (b.month || 0);
      });

    if (outputRecords.length === 0) {
      alert('No output VAT records found for the selected filter.');
      return;
    }

    // Build totals for GRAND TOTAL row
    const totals = { 6: 0, 7: 0, 8: 0, 9: 0, 10: 0 };

    const dataRows = outputRecords.map((r) => {
      const gross = Number(r.grossAmount || 0);
      const exempt = r.isVatExempt ? gross : 0;
      const zeroRated = r.isZeroRated ? gross : 0;
      const taxable = (!r.isVatExempt && !r.isZeroRated) ? Number(r.netAmount || 0) : 0;
      const outputTax = Number(r.vatAmount || 0);

      // Determine registered name vs buyer name based on classification
      const registeredName = r.customerClassification === 'corporation'
        ? String(r.customerName || '').toUpperCase()
        : '';
      const buyerName = (r.customerClassification === 'individual' || !r.customerClassification)
        ? String(r.customerName || '').toUpperCase()
        : '';

      totals[6] += gross;
      totals[7] += exempt;
      totals[8] += zeroRated;
      totals[9] += taxable;
      totals[10] += outputTax;

      return [
        cell(getLastDayOfMonth(r.month, r.fiscalYear)),
        cell(r.customerTin || ''),
        cell(registeredName),
        cell(buyerName),
        cell(resolveAddress(r.customerAddress)),
        numCell(gross),
        numCell(exempt),
        numCell(zeroRated),
        numCell(taxable),
        numCell(outputTax),
      ].join(',');
    });

    const clientName = String(client?.name || '').toUpperCase();
    const clientTin = client?.tin || '';
    const clientTradeName = String(client?.tradeName || client?.name || '').toUpperCase();
    const clientAddress = resolveAddress(
      client?.address ||
      client?.businessAddress ||
      client?.contactInfo ||
      ''
    );

    const rows = [
      'SALES TRANSACTION',
      'RECONCILIATION OF LISTING FOR ENFORCEMENT',
      '',
      `TIN: ${clientTin}`,
      `OWNER'S NAME: ${clientName}`,
      `OWNER'S TRADE NAME: ${clientTradeName}`,
      `OWNER'S ADDRESS: ${clientAddress}`,
      '',
      [
        cell('TAXABLE MONTH'),
        cell('TAXPAYER IDENTIFICATION NUMBER'),
        cell('REGISTERED NAME'),
        cell('NAME OF BUYER (LAST NAME, FIRST NAME, MIDDLE NAME)'),
        cell('BUYER\'S ADDRESS'),
        cell('AMOUNT OF GROSS SALES'),
        cell('AMOUNT OF EXEMPT SALES'),
        cell('AMOUNT OF ZERO-RATED SALES'),
        cell('AMOUNT OF TAXABLE SALES'),
        cell('OUTPUT TAX'),
      ].join(','),
      '(1),(2),(3),(4),(5),(6),(7),(8),(9),(10)',
      ...dataRows,
      [
        cell('GRAND TOTAL'),
        '',
        '',
        '',
        '',
        numCell(totals[6]),
        numCell(totals[7]),
        numCell(totals[8]),
        numCell(totals[9]),
        numCell(totals[10]),
      ].join(','),
      'END OF REPORT',
    ];

    const csv = rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `SLSS_${clientName}_${Date.now()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    recordAttachmentExport(ATTACHMENT_TYPES.SLSP_OUTPUT, totals[10]);
  }, [client?.name, client?.tin, client?.tradeName, client?.address, client?.businessAddress, client?.contactInfo, filteredRecords, getRecordKind, getLastDayOfMonth, fmt, numCell, resolveAddress, cell, recordAttachmentExport]);

  const exportSawtDat = useCallback(() => {
    // SAWT is built from creditable 2307s only — 2306 is final tax and is never
    // a credit. Voided certificates are not credits either.
    const certificates = filteredRecords().filter(
      (r) => getRecordKind(r) === 'form2307' && !r.isVoided
    );

    if (certificates.length === 0) {
      alert('No Form 2307 records found in the current filtered view.');
      return;
    }

    const payeeOf = (record) => ({
      name: record.vendor?.name || record.vendorName || record.payeeName || '',
      tin: record.vendor?.tin || record.vendorTin || record.payeeTin || '',
    });

    // A blank ATC cannot be routed to the VAT or the income-tax side, so it
    // blocks the file. Never default it.
    const missingATCs = findMissingATCs(certificates.map((r) => ({
      name: payeeOf(r).name,
      atcCode: r.atcCode,
      certificateNumber: r.certificateNumber,
    })));
    if (missingATCs.length) {
      alert(missingATCMessage(missingATCs));
      return;
    }

    // A 2550Q SAWT lists only VAT withheld at source (WV). Expanded-withholding
    // 2307s are income-tax credits and belong on the 1701Q/1702Q SAWT.
    const form2307 = certificates.filter((r) => isVatWithholdingAtc(r.atcCode));
    if (form2307.length === 0) {
      alert(
        'None of these Form 2307 records carry a VAT withholding ATC (WV010/WV020). '
        + 'A 2550Q SAWT lists only VAT withheld at source; expanded-withholding 2307s '
        + 'belong on the income tax SAWT.'
      );
      return;
    }

    const clientTIN = cleanTIN(client?.tin);
    if (!clientTIN) {
      alert('Client TIN is required for DAT export. Please update the client profile.');
      return;
    }

    // A missing payee TIN must block the file, never be padded into 000000000.
    const missingTINs = findMissingTINs(form2307.map(payeeOf));
    if (missingTINs.length) {
      alert(missingTINMessage(missingTINs));
      return;
    }

    // FTYPE_CODE is the filer's entity type (1701/1702), never a return code.
    const ftypeCode = resolveFtypeCode(client?.clientType);
    if (!ftypeCode) {
      alert(missingFtypeMessage(client?.name));
      return;
    }

    // A missing RDO blocks the file — never a placeholder RDO (BUG-7).
    const rdoCode = filerRdoCode(client);
    if (!rdoCode) {
      alert(missingRdoMessage(client?.name));
      return;
    }

    // Header period = latest record's month (passed through; period selection
    // is a separate task). Each detail row keeps its own record period.
    const sortedRecords = [...form2307].sort((a, b) => {
      if (a.fiscalYear !== b.fiscalYear) return b.fiscalYear - a.fiscalYear;
      return b.month - a.month;
    });
    const latestRecord = sortedRecords[0];
    const exportMonth = latestRecord?.month || new Date().getMonth() + 1;
    const exportYear = latestRecord?.fiscalYear || new Date().getFullYear();

    const result = buildSawtDat({
      filer: {
        tin: client?.tin,
        branchCode: datFilerBranch(client?.branchCode),
        rdoCode,
        clientType: client?.clientType,
        name: client?.name,
      },
      period: { month: exportMonth, year: exportYear },
      rows: form2307.map((record) => {
        const payee = payeeOf(record);
        const isCorpPayer =
          record.vendor?.businessType === 'corporation' ||
          record.vendorClassification === 'corporation';
        return {
          tin: payee.tin,
          branchCode: '0000',
          payeeEntity: isCorpPayer ? 'corporation' : 'individual',
          name: payee.name,
          period: { month: record.month || exportMonth, year: record.fiscalYear || exportYear },
          atcCode: record.atcCode,
          ratePercent: Number(record.withholdingRate || 0),
          baseAmount: Number(record.baseAmount || 0),
          taxWithheld: Number(record.taxWithheld || 0),
        };
      }),
    });
    if (!result.ok) {
      alert(result.errors.join('\n'));
      return;
    }
    downloadSawtDat(result);

    const totalWithheld = form2307.reduce((sum, r) => sum + Number(r.taxWithheld || 0), 0);

    // File is on disk — only now is the attachment genuinely "exported".
    // totalWithheld is the sum of exactly the WV 2307 rows written above, which
    // is what the filing card reconciles against its claimed 2307 VAT credits.
    recordAttachmentExport(ATTACHMENT_TYPES.SAWT, totalWithheld);
  }, [filteredRecords, getRecordKind, client, recordAttachmentExport]);

  const getCompanyInfo = useCallback(() => {
    const address = client?.address || {};
    
    let street = "";
    let city = "";
    let province = "";
    let zipCode = "";

    if (typeof address === "string" && address.trim()) {
      // Address stored as a single string — use as addressLine1
      street = address.trim();
    } else if (typeof address === "object") {
      street = [
        address.building,
        address.street,
        address.barangay,
        address.substreet,
      ].filter(Boolean).join(" ").trim();
      
      city = address.city || address.cityMunicipality || address.district || "";
      province = address.province || "";
      zipCode = address.zipCode || address.zip || "";
    }

    // Fallback: check top-level client fields
    if (!street) street = client?.streetAddress || client?.addressLine1 || "";
    if (!city) city = client?.city || client?.cityMunicipality || "";
    if (!province) province = client?.province || "";
    if (!zipCode) zipCode = client?.zipCode || client?.zip || "";

    const cityStateZip = [city, province, zipCode].filter(Boolean).join(" ").trim();

    return {
      tin: padTIN(client?.tin || client?.TIN || ""),
      name: client?.name || client?.companyName || client?.registeredName || "",
      addressLine1: street,
      city: city,
      province: province,
      zip: zipCode,
      cityStateZip: cityStateZip,
      rdoCode: String(client?.rdoCode || client?.RDOCode || "").trim(),
    };
  }, [client, padTIN]);

  const getQuarterEndDate = useCallback((quarter, year, month = null) => {
    if (month) {
      // Specific month — get last day of that month
      const lastDay = new Date(year, month, 0).getDate();
      return `${String(month).padStart(2, "0")}/${String(lastDay).padStart(2, "0")}/${year}`;
    }
    // Full quarter
    const quarterEnds = {
      1: "03/31",
      2: "06/30",
      3: "09/30",
      4: "12/31",
    };
    return `${quarterEnds[quarter] || "12/31"}/${year}`;
  }, []);

  const getQuarterFileSuffix = useCallback((quarter, year, month = null) => {
    if (month) {
      // Specific month selected — use that month
      return `${String(month).padStart(2, "0")}${year}`;
    }
    // Full quarter — use quarter-end month
    const monthMap = { 1: "03", 2: "06", 3: "09", 4: "12" };
    return `${monthMap[quarter] || "12"}${year}`;
  }, []);

  const getDateStringForDat = useCallback((date) => {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${mm}/${dd}/${d.getFullYear()}`;
  }, []);

  const isZeroRatedRecord = useCallback((record) => {
    return Boolean(
      record?.isZeroRated ||
      record?.zeroRated ||
      record?.zeroRatedAmount ||
      (!isVatExempt(record) && Number(record?.vatAmount) === 0 && Number(record?.grossAmount) > 0 && record.recordType === "output")
    );
  }, [isVatExempt]);

  const isExemptRecord = useCallback((record) => {
    return Boolean(
      isVatExempt(record) ||
      Number(record?.vatExemptAmount) > 0 ||
      Number(record?.vatExempt) > 0
    );
  }, [isVatExempt]);

  const getExportableVatRecords = useCallback((quarter, year, month = null) => {
    return (records || []).filter((record) => {
      if (getRecordKind(record) !== "vat") return false;
      const period = getPeriod(record);
      if (period.quarter !== quarter || period.fiscalYear !== year) return false;
      if (month !== null && Number(record?.month) !== month) return false;
      return true;
    });
  }, [records, getPeriod, getRecordKind]);

  // Helper to parse address into city/province parts
  const getAddressParts = useCallback((record, type) => {
    const fullAddress = type === "S"
      ? (record?.customerAddress || "")
      : (record?.supplierAddress || "");

    if (!fullAddress.trim()) {
      return { city: "", province: "", zip: "" };
    }

    const parts = fullAddress.split(",").map(p => p.trim()).filter(Boolean);

    if (parts.length >= 3) {
      return { city: parts[parts.length - 2] || "", province: parts[parts.length - 1] || "", zip: "" };
    } else if (parts.length === 2) {
      return { city: parts[0] || "", province: parts[1] || "", zip: "" };
    } else {
      return { city: parts[0] || "", province: "", zip: "" };
    }
  }, []);

  // Resolve vendor/customer details from linked records (with fallback to stored fields)
  const resolveCounterpart = useCallback((record, type) => {
    if (type === "S") {
      // Sales: resolve from customer
      const cust = typeof record.customer === 'object' ? record.customer : null;
      return {
        tin: padTIN(cust?.tin || record?.customerTin || "000000001"),
        name: cust?.name || record?.customerName || record?.description || "",
        address: cust ? (cust.address || cust.businessAddress || cust.contactInfo || "") : (record?.customerAddress || ""),
      };
    } else {
      // Purchases: resolve from vendor
      const vend = typeof record.vendor === 'object' ? record.vendor : null;
      return {
        tin: padTIN(vend?.tin || record?.vendorTin || "000000000"),
        name: vend?.name || record?.vendorName || record?.description || "",
        address: vend ? (vend.address || vend.businessAddress || vend.contactInfo || "") : (record?.supplierAddress || ""),
      };
    }
  }, [padTIN]);

  // Parse address string into city/province components for DAT format
  const parseAddressForDAT = useCallback((addressString) => {
    if (!addressString || !addressString.trim()) {
      return { street: "", city: "", province: "" };
    }

    const parts = addressString.split(",").map(p => p.trim()).filter(Boolean);

    if (parts.length >= 3) {
      return {
        street: parts.slice(0, -2).join(", "),
        city: parts[parts.length - 2],
        province: parts[parts.length - 1],
      };
    } else if (parts.length === 2) {
      return { street: "", city: parts[0], province: parts[1] };
    } else {
      return { street: "", city: parts[0], province: "" };
    }
  }, []);

  const cleanBIRName = (name) => (name || '').replace(/,/g, '').trim();

  const generateBIRReliefDAT = useCallback((recordsToExport, type, companyInfo, period, month = null) => {
    const companyTin = companyInfo.tin;
    const companyName = companyInfo.name;
    const addressLine1 = companyInfo.addressLine1;
    const cityStateZip = companyInfo.cityStateZip;
    const periodEnd = getQuarterEndDate(period.quarter, period.year, month);

    const quote = quoteString;
    const sellerTin = companyTin;
    const buyerTinDefault = "000000001";

    const detailRows = recordsToExport.map((record) => {
      const recordValues = getDisplayValues(record);
      const gross = Number(recordValues.grossAmount) || 0;
      const vat = Number(recordValues.taxAmount) || 0;
      const transactionDate = formatTaxableMonth(record);
      
      // Resolve counterpart from linked record with fallback to stored fields
      const counterpart = resolveCounterpart(record, type);
      const counterpartTin = counterpart.tin;
      const counterpartName = cleanBIRName(counterpart.name);
      const addrParts = parseAddressForDAT(counterpart.address);
      const city = addrParts.city || companyInfo.city || "";
      const province = addrParts.province || companyInfo.province || "";
      const zip = addrParts.zip || companyInfo.zip || "";
      
      // Calculate amounts (not flags)
      const zeroRatedAmount = isZeroRatedRecord(record) ? gross : 0;
      const exemptAmount = isExemptRecord(record) ? gross : 0;
      const isNormalVatable = !isZeroRatedRecord(record) && !isExemptRecord(record);

      if (type === "S") {
        const taxableSales = isNormalVatable ? (gross - vat) : 0;
        return [
          "D",
          "S",
          quote(counterpartTin || buyerTinDefault),
          quote(counterpartName),
          "",
          "",
          "",
          quote(city),
          quote(zip),
          zeroRatedAmount.toFixed(2),
          exemptAmount.toFixed(2),
          taxableSales.toFixed(2),
          vat.toFixed(2),
          sellerTin,
          transactionDate,
        ].join(",");
      }

      // Purchase (type P): break down by purchase type
      const serviceAmount = isNormalVatable && record?.purchaseType === 'services' ? gross : 0;
      const capitalGoodsAmount = isNormalVatable && record?.purchaseType === 'capital_goods' ? gross : 0;
      const goodsOtherAmount = isNormalVatable && (record?.purchaseType === 'goods_other' || !record?.purchaseType) ? gross : 0;

      return [
        "D",
        "P",
        quote(counterpartTin || buyerTinDefault),
        quote(counterpartName),
        "",
        "",
        "",
        quote(city),
        quote(province),
        exemptAmount.toFixed(2),
        zeroRatedAmount.toFixed(2),
        serviceAmount.toFixed(2),
        capitalGoodsAmount.toFixed(2),
        goodsOtherAmount.toFixed(2),
        vat.toFixed(2),
        sellerTin,
        transactionDate,
      ].join(",");
    });

    const recordCount = detailRows.length;
    const totals = recordsToExport.reduce((acc, record) => {
      const recordValues = getDisplayValues(record);
      const gross = Number(recordValues.grossAmount) || 0;
      const vat = Number(recordValues.taxAmount) || 0;
      const netOfVAT = Number(recordValues.netAmount) || 0;
      const zeroRated = isZeroRatedRecord(record) ? gross : 0;
      const exempt = isExemptRecord(record) ? gross : 0;
      const vatable = zeroRated || exempt ? 0 : gross;
      if (type === "S") {
        acc.zeroRated += zeroRated;
        acc.exempt += exempt;
        acc.vatable += vatable;
        acc.tax += vat;
        acc.netOfVAT += netOfVAT;
      } else {
        const capitalGoods = /capital|equipment/i.test(record.description || record.chartOfAccountName || "") ? gross : 0;
        acc.zeroRated += zeroRated;
        acc.exempt += exempt;
        acc.services += 0;
        acc.capitalGoods += capitalGoods;
        acc.vatable += vatable;
        acc.tax += vat;
        acc.claimed += vat;
      }
      return acc;
    }, {
      zeroRated: 0,
      exempt: 0,
      services: 0,
      capitalGoods: 0,
      vatable: 0,
      tax: 0,
      claimed: 0,
      netOfVAT: 0,
    });

    const headerFields = type === "S"
      ? [
          "H",
          "S",
          quote(companyTin),
          quote(companyName),
          "",
          "",
          "",
          quote(companyName),
          quote(addressLine1),
          quote(cityStateZip),
          totals.zeroRated.toFixed(2),
          totals.exempt.toFixed(2),
          totals.netOfVAT.toFixed(2),
          totals.tax.toFixed(2),
          companyInfo.rdoCode,
          periodEnd,
          recordCount,
        ]
      : [
          "H",
          "P",
          quote(companyTin),
          quote(companyName),
          "",
          "",
          "",
          quote(companyName),
          quote(addressLine1),
          quote(cityStateZip),
          totals.exempt.toFixed(2),
          totals.zeroRated.toFixed(2),
          totals.services.toFixed(2),
          totals.capitalGoods.toFixed(2),
          totals.vatable.toFixed(2),
          totals.tax.toFixed(2),
          totals.claimed.toFixed(2),
          (0).toFixed(2),
          companyInfo.rdoCode,
          periodEnd,
          recordCount,
        ];

    const headerLine = headerFields.join(",");
    return [headerLine, ...detailRows].join("\r\n");
  }, [client, formatTaxableMonth, getAddressParts, getCompanyInfo, getDateStringForDat, getDisplayValues, getQuarterEndDate, isExemptRecord, isZeroRatedRecord, padTIN, quoteString, resolveCounterpart, parseAddressForDAT]);

  const downloadDATFile = useCallback((content, filename) => {
    const blob = new Blob([content], { type: "text/plain;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  const parsePeriodFilterValue = useCallback(() => {
    if (periodFilter === "all") return null;
    const [quarter, year] = periodFilter.split("-");
    return { quarter: Number(quarter), year: Number(year) };
  }, [periodFilter]);

  const prepareDatExportRecords = useCallback((period, month = null) => {
    return getExportableVatRecords(period.quarter, period.year, month);
  }, [getExportableVatRecords]);

  const handleExportBIRDAT = useCallback(() => {
    const period = parsePeriodFilterValue();
    if (!period) {
      if (uniquePeriods.length === 0) {
        alert("No VAT periods available to export.");
        return;
      }
      setExportQuarter(uniquePeriods[0].quarter);
      setExportYear(uniquePeriods[0].year);
      setShowExportDatDialog(true);
      return;
    }

    const exportPeriod = period;
    const availableRecords = prepareDatExportRecords(exportPeriod, selectedMonth);
    if (availableRecords.length === 0) {
      alert(`No VAT records found for Q${exportPeriod.quarter} ${exportPeriod.year}.`);
      return;
    }
    const companyInfo = getCompanyInfo();
    if (!companyInfo.tin || companyInfo.tin.length !== 9) {
      alert("Company TIN must be 9 digits before exporting DAT files.");
      return;
    }
    if (!companyInfo.rdoCode) {
      alert("RDO Code is required before exporting DAT files.");
      return;
    }

    const zeroVatRecords = availableRecords.filter((record) => Number(getDisplayValues(record).taxAmount) === 0);
    if (zeroVatRecords.length > 0) {
      alert("Warning: Some records have zero or missing VAT amount. Files will still be exported.");
    }

    setExportingDat(true);
    try {
      const salesRecords = availableRecords.filter((record) => record.recordType === "output");
      const purchaseRecords = availableRecords.filter((record) => record.recordType === "input");
      const salesContent = generateBIRReliefDAT(salesRecords, "S", companyInfo, exportPeriod, selectedMonth);
      const purchasesContent = generateBIRReliefDAT(purchaseRecords, "P", companyInfo, exportPeriod, selectedMonth);
      const periodSuffix = getQuarterFileSuffix(exportPeriod.quarter, exportPeriod.year, selectedMonth);
      downloadDATFile(salesContent, `${companyInfo.tin}S${periodSuffix}.DAT`);
      downloadDATFile(purchasesContent, `${companyInfo.tin}P${periodSuffix}.DAT`);
      alert("BIR RELIEF DAT files exported successfully");
    } catch (error) {
      console.error("BIR DAT export failed", error);
      alert("Failed to export BIR RELIEF DAT files.");
    } finally {
      setExportingDat(false);
    }
  }, [downloadDATFile, generateBIRReliefDAT, getCompanyInfo, getDisplayValues, getExportableVatRecords, parsePeriodFilterValue, prepareDatExportRecords, uniquePeriods, getQuarterFileSuffix, selectedMonth]);

  const handleConfirmExportDat = useCallback(() => {
    if (!exportQuarter || !exportYear) {
      alert("Please choose a quarter to export.");
      return;
    }
    setShowExportDatDialog(false);
    const period = { quarter: exportQuarter, year: exportYear };
    const availableRecords = prepareDatExportRecords(period, selectedMonth);
    if (availableRecords.length === 0) {
      alert(`No VAT records found for Q${period.quarter} ${period.year}.`);
      return;
    }

    const companyInfo = getCompanyInfo();
    if (!companyInfo.tin || companyInfo.tin.length !== 9) {
      alert("Company TIN must be 9 digits before exporting DAT files.");
      return;
    }
    if (!companyInfo.rdoCode) {
      alert("RDO Code is required before exporting DAT files.");
      return;
    }

    const zeroVatRecords = availableRecords.filter((record) => Number(getDisplayValues(record).taxAmount) === 0);
    if (zeroVatRecords.length > 0) {
      alert("Warning: Some records have zero or missing VAT amount. Files will still be exported.");
    }

    setExportingDat(true);
    try {
      const salesRecords = availableRecords.filter((record) => record.recordType === "output");
      const purchaseRecords = availableRecords.filter((record) => record.recordType === "input");
      const salesContent = generateBIRReliefDAT(salesRecords, "S", companyInfo, period, selectedMonth);
      const purchasesContent = generateBIRReliefDAT(purchaseRecords, "P", companyInfo, period, selectedMonth);
      const periodSuffix = getQuarterFileSuffix(period.quarter, period.year, selectedMonth);
      downloadDATFile(salesContent, `${companyInfo.tin}S${periodSuffix}.DAT`);
      downloadDATFile(purchasesContent, `${companyInfo.tin}P${periodSuffix}.DAT`);
      setShowExportDatDialog(false);
      alert("BIR RELIEF DAT files exported successfully");
      if (!hideValidationGuide) {
        setShowValidationGuide(true);
      }
    } catch (error) {
      console.error("BIR DAT export failed", error);
      alert("Failed to export BIR RELIEF DAT files.");
    } finally {
      setExportingDat(false);
    }
  }, [downloadDATFile, exportQuarter, exportYear, generateBIRReliefDAT, getCompanyInfo, getDisplayValues, getExportableVatRecords, getQuarterFileSuffix, prepareDatExportRecords, selectedMonth, hideValidationGuide]);

  /* ==========================
      EDIT (VAT + 2306)
  ========================== */
  const handleEditClick = useCallback(
    (record) => {
      const kind = getRecordKind(record);
      if (kind === "vat" && !isEditableVatRecord(record)) return;
      if (kind === "form2306" && !isEditableForm2306Record(record)) return;

      setEditingId(record._id);

      if (kind === "vat") {
        setEditingData({
          kind: "vat",
          recordType: record.recordType,
          description: record.description || "",
          grossAmount: record.grossAmount || 0,
          vatAmount: record.vatAmount || 0,
          netAmount: record.netAmount || 0,
          vatExemptAmount: record.vatExemptAmount || 0,
          isVatExempt: record.isVatExempt || false,
          isZeroRated: record.isZeroRated || false,
          isService: record.isService || false,
          purchaseType: record.purchaseType || 'goods_other',
          vendor: record.vendor || null,
          vendorName: record.vendorName || "",
          vendorTin: record.vendorTin || "",
          vendorClassification: record.vendorClassification || "",
          supplierAddress: record.supplierAddress || "",
          customer: record.customer || null,
          customerName: record.customerName || "",
          customerTin: record.customerTin || "",
          customerClassification: record.customerClassification || "",
          customerAddress: record.customerAddress || "",
          chartOfAccount: record.chartOfAccount || null,
          chartOfAccountName: record.chartOfAccountName || "",
          transactionDate: record.transactionDate ? new Date(record.transactionDate).toISOString().split("T")[0] : "",
          // Snapshot taken once when editing starts — must not re-derive from live
          // content, or the field would lock itself after the user's first keystroke.
          supplierAddressLocked: !!(record.vendor && record.supplierAddress),
          customerAddressLocked: !!(record.customer && record.customerAddress),
        });
      } else {
        // form2306: amount edit only
        const baseAmount = Number(record?.baseAmount ?? record?.grossAmount ?? 0) || 0;
        const withholdingRate = Number(record?.withholdingRate ?? record?.withheldRate ?? 0) || 0;
        const taxWithheld = Number(record?.taxWithheld ?? record?.withheldAmount ?? 0) || 0;

        setEditingData({
          kind: "form2306",
          baseAmount,
          withholdingRate,
          taxWithheld,
          transactionDate: record.transactionDate ? new Date(record.transactionDate).toISOString().split("T")[0] : "",
        });
      }
    },
    [getRecordKind, isEditableVatRecord, isEditableForm2306Record]
  );

  const handleCancelEdit = useCallback(() => {
    setEditingId(null);
    setEditingData(null);
  }, []);

  const handleEditChange = useCallback((field, value) => {
    setEditingData((prev) => ({ ...(prev || {}), [field]: value }));
  }, []);

  const handleChartOfAccountSelect = useCallback(
    (account) => {
      if (!account) {
        handleEditChange("chartOfAccount", null);
        handleEditChange("chartOfAccountName", "");
        return;
      }
      handleEditChange("chartOfAccount", account._id);
      handleEditChange("chartOfAccountName", `${account.accountNumber} - ${account.accountName}`);
      if (!editingData?.description) handleEditChange("description", account.accountName);
    },
    [editingData, handleEditChange]
  );

  // VAT gross change (same as your logic)
  const handleVatGrossAmountChange = useCallback(
    (value) => {
      const cleaned = String(value).replace(/[^0-9.]/g, '');
      const grossAmount = parseFloat(cleaned) || 0;

      if (editingData?.isZeroRated) {
        // Zero-rated: no VAT extraction, net = gross
        handleEditChange("grossAmount", grossAmount);
        handleEditChange("vatAmount", 0);
        handleEditChange("netAmount", grossAmount);
        handleEditChange("vatExemptAmount", 0);
      } else if (editingData?.isVatExempt) {
        // VAT exempt: no VAT extraction, net = gross
        handleEditChange("grossAmount", grossAmount);
        handleEditChange("vatAmount", 0);
        handleEditChange("netAmount", grossAmount);
        handleEditChange("vatExemptAmount", grossAmount);
      } else {
        // Normal: VAT extraction, net = gross - vat
        const vatAmount = grossAmount > 0 ? (grossAmount / 1.12) * 0.12 : 0;
        const netAmount = grossAmount - vatAmount;

        handleEditChange("grossAmount", grossAmount);
        handleEditChange("vatAmount", parseFloat(vatAmount.toFixed(2)));
        handleEditChange("netAmount", parseFloat(netAmount.toFixed(2)));
        handleEditChange("vatExemptAmount", 0);
      }
    },
    [editingData, handleEditChange]
  );

  const handleVatExemptToggle = useCallback(
    (isExempt) => {
      handleEditChange("isVatExempt", isExempt);
      
      // Reset isZeroRated when setting isVatExempt (mutual exclusivity)
      if (isExempt) {
        handleEditChange("isZeroRated", false);
      }

      const grossAmount = parseFloat(editingData?.grossAmount) || 0;

      if (isExempt) {
        handleEditChange("vatAmount", 0);
        handleEditChange("netAmount", grossAmount);
        handleEditChange("vatExemptAmount", grossAmount);
      } else {
        const vatAmount = grossAmount > 0 ? (grossAmount / 1.12) * 0.12 : 0;
        const netAmount = grossAmount - vatAmount;

        handleEditChange("vatAmount", parseFloat(vatAmount.toFixed(2)));
        handleEditChange("netAmount", parseFloat(netAmount.toFixed(2)));
        handleEditChange("vatExemptAmount", 0);
      }
    },
    [editingData, handleEditChange]
  );

  const handleVatZeroRatedToggle = useCallback(
    (isZeroRated) => {
      handleEditChange("isZeroRated", isZeroRated);
      
      // Reset isVatExempt when setting isZeroRated (mutual exclusivity)
      if (isZeroRated) {
        handleEditChange("isVatExempt", false);
      }

      const grossAmount = parseFloat(editingData?.grossAmount) || 0;

      if (isZeroRated) {
        handleEditChange("vatAmount", 0);
        handleEditChange("netAmount", grossAmount);
        handleEditChange("vatExemptAmount", 0);
      } else {
        const vatAmount = grossAmount > 0 ? (grossAmount / 1.12) * 0.12 : 0;
        const netAmount = grossAmount - vatAmount;

        handleEditChange("vatAmount", parseFloat(vatAmount.toFixed(2)));
        handleEditChange("netAmount", parseFloat(netAmount.toFixed(2)));
        handleEditChange("vatExemptAmount", 0);
      }
    },
    [editingData, handleEditChange]
  );

  // Form2306 base change -> recompute withheld using stored withholdingRate
  const handle2306BaseAmountChange = useCallback(
    (value) => {
      const cleaned = String(value).replace(/[^0-9.]/g, '');
      const baseAmount = parseFloat(cleaned) || 0;
      const rate = parseFloat(editingData?.withholdingRate) || 0;
      const taxWithheld = (baseAmount * rate) / 100;
      handleEditChange("baseAmount", baseAmount);
      handleEditChange("taxWithheld", parseFloat(taxWithheld.toFixed(2)));
    },
    [editingData, handleEditChange]
  );

  const handleSaveEdit = useCallback(async () => {
    if (!editingData || !editingId) return;

    try {
      setSaveLoading(true);

      const originalRecord = (records || []).find((r) => r._id === editingId);
      if (!originalRecord) throw new Error("Record not found");

      if (editingData.kind === "vat") {
        if (!editingData.description?.trim()) {
          alert("Description is required");
          setSaveLoading(false);
          return;
        }

        const updateData = {
          description: editingData.description?.trim() || '',
          grossAmount: Number(editingData.grossAmount) || 0,
          vatAmount: Number(editingData.vatAmount) || 0,
          netAmount: Number(editingData.netAmount) || 0,
          vatExemptAmount: editingData.isVatExempt ? Number(editingData.grossAmount) || 0 : 0,
          isVatExempt: Boolean(editingData.isVatExempt),
          chartOfAccount: editingData.chartOfAccount || null,
          chartOfAccountName: editingData.chartOfAccountName || "",
          transactionDate: editingData.transactionDate || new Date().toISOString(),
          // New vendor/customer fields
          vendor: editingData.vendor || null,
          vendorName: editingData.vendorName || "",
          vendorTin: editingData.vendorTin || "",
          vendorClassification: editingData.vendorClassification || "",
          supplierAddress: editingData.supplierAddress || "",
          customer: editingData.customer || null,
          customerName: editingData.customerName || "",
          customerTin: editingData.customerTin || "",
          customerClassification: editingData.customerClassification || "",
          customerAddress: editingData.customerAddress || "",
          purchaseType: editingData.purchaseType || 'goods_other',
          isService: Boolean(editingData.isService),
          isZeroRated: Boolean(editingData.isZeroRated),
        };

        console.log('[VAT Edit Save] updateData being sent:', updateData);

        const response = await vatAPI.update(editingId, updateData);
        console.log('[VATRecordsList] vatAPI.update payload raw:', response);
        console.log('[VATRecordsList] vatAPI.update payload.data:', response?.data);
        const updatedRecord = response?.data?.data ?? response?.data ?? originalRecord;

        const mergedRecord = {
          ...originalRecord,
          ...updatedRecord,
          grossAmount: updatedRecord?.grossAmount ?? updateData.grossAmount,
          vatAmount: updatedRecord?.vatAmount ?? updateData.vatAmount,
          netAmount: updatedRecord?.netAmount ?? updateData.netAmount,
          description: updatedRecord?.description ?? updateData.description,
          chartOfAccount: updatedRecord?.chartOfAccount ?? updateData.chartOfAccount,
          transactionDate: updatedRecord?.transactionDate ?? updateData.transactionDate,
          isPostedToGL: updatedRecord?.isPostedToGL ?? originalRecord?.isPostedToGL,
          glJournalEntry: updatedRecord?.glJournalEntry ?? originalRecord?.glJournalEntry,
          // Include new fields
          vendor: updatedRecord?.vendor ?? updateData.vendor,
          vendorName: updatedRecord?.vendorName ?? updateData.vendorName,
          vendorTin: updatedRecord?.vendorTin ?? updateData.vendorTin,
          vendorClassification: updatedRecord?.vendorClassification ?? updateData.vendorClassification,
          supplierAddress: updatedRecord?.supplierAddress ?? updateData.supplierAddress,
          customer: updatedRecord?.customer ?? updateData.customer,
          customerName: updatedRecord?.customerName ?? updateData.customerName,
          customerTin: updatedRecord?.customerTin ?? updateData.customerTin,
          customerClassification: updatedRecord?.customerClassification ?? updateData.customerClassification,
          customerAddress: updatedRecord?.customerAddress ?? updateData.customerAddress,
          purchaseType: updatedRecord?.purchaseType ?? updateData.purchaseType,
          isService: updatedRecord?.isService ?? updateData.isService,
          isZeroRated: updatedRecord?.isZeroRated ?? updateData.isZeroRated,
        };

        console.log('[VATRecordsList] mergedRecord for onRecordUpdated:', {
          _id: mergedRecord?._id,
          grossAmount: mergedRecord?.grossAmount,
          vatAmount: mergedRecord?.vatAmount,
          netAmount: mergedRecord?.netAmount,
          isPostedToGL: mergedRecord?.isPostedToGL,
          glJournalEntry: mergedRecord?.glJournalEntry,
        });

        await onRecordUpdated?.(mergedRecord);
      } else {
        // ✅ Form 2306 amount-only update
        const update2306 = {
          baseAmount: parseFloat(editingData.baseAmount) || 0,
          taxWithheld: parseFloat(editingData.taxWithheld) || 0,
          transactionDate: editingData.transactionDate || originalRecord.transactionDate || new Date().toISOString(),
        };

        await formAPI.updateForm2306Record(editingId, update2306);
        onRecordUpdated?.();
      }

      setEditingId(null);
      setEditingData(null);
    } catch (error) {
      console.error("Error saving record:", error);
      alert(`Failed to save: ${error?.message || "Unknown error"}`);
    } finally {
      setSaveLoading(false);
    }
  }, [editingData, editingId, records, onRecordUpdated]);

  const handleDelete = useCallback((record) => {
    setPendingDelete(record);
  }, []);

  const confirmDelete = useCallback(async () => {
    const record = pendingDelete;
    if (!record) return;
    const kind = getRecordKind(record);

    try {
      setDeletingId(record._id);

      if (kind === "vat") await vatAPI.delete(record._id);
      else await formAPI.deleteForm2306Record(record._id);

      onRecordDeleted?.();
    } catch (error) {
      console.error("Error deleting record:", error);
      notify.error("Couldn't delete", error?.message || "Unknown error — try again.");
    } finally {
      setDeletingId(null);
      setPendingDelete(null);
    }
  }, [pendingDelete, getRecordKind, onRecordDeleted]);

  const handleOpenGLAudit = useCallback(async (record) => {
    if (!client?._id || !record?._id) return;

    try {
      setAuditOpen(true);
      setAuditLoading(true);
      setAuditError("");
      setAuditRecord(record);

      const response = await vatAPI.getGLAuditTrail(client._id, record._id);
      setAuditData(response?.data || null);
    } catch (error) {
      console.error("Error loading VAT GL audit trail:", error);
      setAuditError(error?.message || "Failed to load GL audit trail");
      setAuditData(null);
    } finally {
      setAuditLoading(false);
    }
  }, [client?._id]);

  const handleCloseGLAudit = useCallback(() => {
    setAuditOpen(false);
    setAuditLoading(false);
    setAuditError("");
    setAuditRecord(null);
    setAuditData(null);
  }, []);

  /* ==========================
      Totals / Export (kept)
  ========================== */
  const calculateTotals = useCallback(() => {
    const current = filteredRecords();
    return current.reduce(
      (totals, record) => {
        const vals = getDisplayValues(record);
        const exempt = isVatExempt(record);

        return {
          gross: totals.gross + (vals.grossAmount || 0),
          tax: totals.tax + (vals.taxAmount || 0),
          net: totals.net + (vals.netAmount || 0),
          count: totals.count + 1,
          exemptCount: totals.exemptCount + (exempt ? 1 : 0),
          form2306Count: totals.form2306Count + (getRecordKind(record) === "form2306" ? 1 : 0),
          form2307Count: totals.form2307Count + (getRecordKind(record) === "form2307" ? 1 : 0),
        };
      },
      { gross: 0, tax: 0, net: 0, count: 0, exemptCount: 0, form2306Count: 0, form2307Count: 0 }
    );
  }, [filteredRecords, getDisplayValues, isVatExempt, getRecordKind]);

  const exportToCSV = useCallback(() => {
    try {
      setExporting(true);

      const current = filteredRecords();
      if (current.length === 0) {
        alert("No records to export.");
        setExporting(false);
        return;
      }

      const headers = [
        "Type",
        "Form",
        "Period",
        "Quarter",
        "Fiscal Year",
        "Description",
        "Chart of Account",
        "Payee Name",
        "Payee TIN",
        "Transaction Date",
        "Gross/Base Amount",
        "Tax Amount (VAT/Withheld)",
        "Net Amount",
      ];

      const csvRows = current.map((record) => {
        const kind = getRecordKind(record);
        const quarterDisplay = getQuarterDisplay(record);
        const vals = getDisplayValues(record);
        const monthLabel = Number(record?.month)
          ? MONTH_OPTIONS.find((m) => m.value === Number(record.month))?.label || ""
          : "";

        const typeLabel =
          kind === "form2306"
            ? "Form 2306"
            : kind === "form2307"
            ? "Form 2307"
            : record.recordType === "input"
            ? "Input VAT"
            : "Output VAT";

        const formLabel = kind === "form2306" ? "2306" : kind === "form2307" ? "2307" : "";
        // A 2307 carries the same vendor/base/tax shape as a 2306, so the same
        // payee reader serves both.
        const payee = kind === "form2306" || kind === "form2307"
          ? getForm2306Payee(record)
          : { name: "", tin: "" };

        return [
          `"${typeLabel}"`,
          `"${formLabel}"`,
          `"${kind === "form2306" && monthLabel ? `${quarterDisplay.fullLabel} - ${monthLabel}` : quarterDisplay.fullLabel}"`,
          `"Q${quarterDisplay.quarter}"`,
          `"${quarterDisplay.fiscalYear}"`,
          `"${record.description || ""}"`,
          `"${record.chartOfAccountName || ""}"`,
          `"${payee.name || ""}"`,
          `"${payee.tin || ""}"`,
          `"${formatDate(record.transactionDate)}"`,
          `"${Number(vals.grossAmount || 0).toFixed(2)}"`,
          `"${Number(vals.taxAmount || 0).toFixed(2)}"`,
          `"${Number(vals.netAmount || 0).toFixed(2)}"`,
        ].join(",");
      });

      const csvContent = [headers.join(","), ...csvRows].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `Records_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert("Export failed.");
    } finally {
      setExporting(false);
    }
  }, [filteredRecords, getRecordKind, getQuarterDisplay, getDisplayValues, getForm2306Payee]);

  const clearAllFilters = useCallback(() => {
    setFilter("all");
    setQuarterFilter("all");
    setYearFilter("all");
    setPeriodFilter("all");
    setMonthFilter("all");
    setSelectedMonth(null);
  }, []);

  const totals = calculateTotals();
  const currentRecords = filteredRecords();
  const hasActiveFilters = filter !== "all" || quarterFilter !== "all" || yearFilter !== "all" || periodFilter !== "all" || selectedMonth !== null;

  if (!records || records.length === 0) {
    return (
      <div className="text-center py-8">
        <h3 className="text-sm font-medium text-ink">No records found</h3>
        <p className="text-sm text-ink-body">No VAT/Form records available.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-ink-canvas p-4 rounded-lg space-y-4">
        {(showFilters || showQuarterFilter) && (
          <>
            {showFilters && (
              <div className="flex flex-wrap gap-2">
                {["all", "input", "output", "form2306", "form2307"].map((t) => (
                  <button
                    key={t}
                    onClick={() => setFilter(t)}
                    className={`px-3 py-1 rounded-md text-sm font-medium ${
                      filter === t
                        ? "bg-primary-500 text-white"
                        : "bg-white text-ink-body border border-ink-line hover:bg-primary-50"
                    }`}
                  >
                    {t === "all"
                      ? "All records"
                      : t === "form2306"
                      ? "Form 2306 (EWT)"
                      : t === "form2307"
                      ? "Form 2307 (CWT)"
                      : t === "input"
                      ? "Input VAT"
                      : "Output VAT"}
                  </button>
                ))}
              </div>
            )}

            {showQuarterFilter && (
            <div className="flex flex-wrap gap-4 items-end">
              {/* QUARTER */}
              <div>
                <label className="block text-xs font-medium text-ink-body mb-1">Quarter</label>
                <select
                  value={periodFilter}
                  onChange={(e) => {
                    setPeriodFilter(e.target.value);
                    setQuarterFilter("all");
                    setYearFilter("all");
                    // reset month filter when quarter changes
                    setSelectedMonth(null);
                  }}
                  className="block rounded-md border-ink-line py-1.5 pl-3 pr-10 text-sm"
                >
                  <option value="all">All Periods</option>
                  {uniquePeriods.map((p) => (
                    <option key={`${p.quarter}-${p.year}`} value={`${p.quarter}-${p.year}`}>
                      {p.label} ({p.count} records)
                    </option>
                  ))}
                </select>
              </div>

              {/* MONTH — only shows months within selected quarter */}
              {periodFilter !== "all" && (
                <div>
                  <label className="block text-xs font-medium text-ink-body mb-1">Month</label>
                  <select
                    value={selectedMonth || ''}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value) || null)}
                    className="block rounded-md border-ink-line py-1.5 pl-3 pr-10 text-sm"
                  >
                    <option value="">All Months</option>
                    {(() => {
                      const [q] = periodFilter.split("-");
                      const quarter = parseInt(q, 10);
                      return QUARTER_MONTHS[quarter]?.map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      )) || [];
                    })()}
                  </select>
                </div>
              )}

              {(filter !== "all" || periodFilter !== "all" || selectedMonth !== null) && (
                <Button
                  onClick={() => { clearAllFilters(); }}
                  variant="ghost"
                  size="sm"
                >
                  Clear all filters
                </Button>
              )}
            </div>
          )}
          </>
        )}

        <div className="pt-4 border-t border-ink-line flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={exportToCSV} disabled={exporting || currentRecords.length === 0} loading={exporting} variant="outline" size="sm" icon={Download}>
              CSV
            </Button>
            <Button onClick={exportSLSP} disabled={exporting || currentRecords.length === 0} variant="outline" size="sm" icon={Download}>
              SLSP (Input)
            </Button>
            <Button onClick={exportSLSS} disabled={exporting || currentRecords.length === 0} variant="outline" size="sm" icon={Download}>
              SLSS (Output)
            </Button>
            <Button onClick={handleExportBIRDAT} disabled={exportingDat} loading={exportingDat} variant="outline" size="sm" icon={Download}>
              BIR DAT
            </Button>
            <Button
              onClick={exportSawtDat}
              disabled={exporting || filteredRecords().filter(r => getRecordKind(r) === 'form2307' && !r.isVoided).length === 0}
              variant="outline"
              size="sm"
              icon={Download}
              title="Export VAT-withholding Form 2307 records as a SAWT DAT file"
            >
              2551M DAT
            </Button>
            <Button
              onClick={() => setColWidths({ ...DEFAULT_COL_WIDTHS })}
              variant="ghost"
              size="sm"
              title="Reset column widths to default"
            >
              Reset columns
            </Button>
          </div>
          <div className="text-sm text-ink-body">
            <span className="font-medium text-ink">
              {selectedMonth !== null && periodFilter !== "all"
                ? (() => {
                    const monthObj = QUARTER_MONTHS[parseInt(periodFilter.split("-")[0], 10)]?.find(m => m.value === selectedMonth);
                    const year = parseInt(periodFilter.split("-")[1], 10);
                    return `${monthObj?.label || ''} ${year} (${currentRecords.length} records)`;
                  })()
                : periodFilter !== "all"
                ? (() => {
                    const [q, y] = periodFilter.split("-");
                    return `Q${q} ${y} (${currentRecords.length} records)`;
                  })()
                : `(${currentRecords.length} records)`
              }
            </span>
            • 2306: <span className="font-medium text-ink">{totals.form2306Count}</span>
          </div>
        </div>
      </div>

      {showExportDatDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-4">Select quarter for BIR DAT export</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quarter</label>
                <select
                  value={`${exportQuarter || uniquePeriods[0]?.quarter}-${exportYear || uniquePeriods[0]?.year}`}
                  onChange={(e) => {
                    const [q, y] = e.target.value.split("-");
                    setExportQuarter(Number(q));
                    setExportYear(Number(y));
                  }}
                  className="w-full rounded-md border-gray-300 py-2 px-3"
                >
                  {uniquePeriods.map((p) => (
                    <option key={`${p.quarter}-${p.year}`} value={`${p.quarter}-${p.year}`}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setShowExportDatDialog(false)} className="px-4 py-2 border rounded-md text-sm">
                Cancel
              </button>
              <button type="button" onClick={handleConfirmExportDat} className="px-4 py-2 bg-sky-600 text-white rounded-md text-sm">
                Export
              </button>
            </div>
          </div>
        </div>
      )}

      {showValidationGuide && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              DAT Files Exported Successfully
            </h3>

            <div className="space-y-4 text-sm text-gray-700">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="font-medium text-green-800">
                  ✅ Your Sales (S) and Purchases (P) DAT files have been downloaded.
                </p>
              </div>

              <div>
                <p className="font-semibold text-gray-900 mb-2">
                  Next Step: Validate with BIR RELIEF Validation Module
                </p>
                <p className="text-xs text-gray-500 mb-3">
                  You do NOT load the DAT file into the RELIEF Data Entry module.
                  The DAT goes directly into the <strong>Validation Module</strong> — 
                  a separate shortcut installed alongside the Data Entry tool.
                </p>
                <ol className="space-y-3 text-gray-600 list-decimal list-inside">
                  <li>
                    Open the <strong>"Relief Validation Module"</strong> shortcut on your desktop
                    <span className="block text-xs text-gray-400 ml-5 mt-0.5">
                      (This is a separate icon from "Relief Data Entry" — both are installed with BIR RELIEF v2.3)
                    </span>
                  </li>
                  <li>
                    Enter the client's <strong>TIN</strong> (9 digits, no dashes)
                  </li>
                  <li>
                    Select the <strong>Reporting Month</strong> — this must match the month
                    in your DAT filename (e.g., "01" for January)
                  </li>
                  <li>
                    Select the <strong>Reporting Type</strong>:
                    <span className="block text-xs text-gray-500 ml-5 mt-0.5">
                      • <strong>"Purchases"</strong> for the P file (e.g., 008789606<strong>P</strong>012026.DAT)<br/>
                      • <strong>"Sales"</strong> for the S file (e.g., 008789606<strong>S</strong>012026.DAT)
                    </span>
                  </li>
                  <li>
                    Click <strong>"Validate File"</strong> → browse to the downloaded DAT file → select it
                  </li>
                  <li>
                    Click <strong>"Validate"</strong> → choose a folder to save the validation report
                  </li>
                  <li>
                    If successful, you'll see <strong>"Validation of file is successful"</strong> 
                    and a Notepad showing "No Errors Encountered"
                    <span className="block text-xs text-gray-400 ml-5 mt-0.5">
                      The module automatically generates the .RLF file and appends a serial number to the DAT file
                    </span>
                  </li>
                  <li>
                    <strong>Repeat steps 2–7</strong> for the other file type (Sales or Purchases)
                  </li>
                  <li>
                    Submit the validated DAT + RLF files via <strong>esubmission@bir.gov.ph</strong> 
                    or attach to EFPS
                    <span className="block text-xs text-gray-400 ml-5 mt-0.5">
                      EFPS users: the Validation Module appends a key to the DAT file during validation — 
                      this is required for EFPS submission
                    </span>
                  </li>
                </ol>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-800 text-xs">
                  <strong>Common mistake:</strong> Do NOT try to import the DAT file into 
                  the "Relief Data Entry" module. The Data Entry module is for manual 
                  encoding only. Your DAT file goes straight to the <strong>Validation Module</strong>.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-blue-800 text-xs">
                  <strong>What happens during validation?</strong> The Validation Module checks 
                  your DAT file for formatting errors, generates the encrypted <strong>.RLF file</strong>, 
                  and appends a serial number to the DAT. Both the DAT and RLF are needed for submission.
                </p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-amber-800 text-xs">
                  <strong>Don't have the BIR RELIEF tool?</strong> Download{' '}
                  <strong>BIR RELIEF v2.3</strong> from{' '}
                  <a
                    href="https://www.bir.gov.ph/Downloadables"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-medium hover:text-amber-900"
                  >
                    bir.gov.ph/Downloadables
                  </a>{' '}
                  → look for "Relief" section. The download includes both the 
                  Data Entry module and the Validation Module.
                </p>
              </div>
            </div>

            <div className="mt-5 flex justify-between items-center">
              <label className="flex items-center gap-2 text-xs text-gray-500">
                <input
                  type="checkbox"
                  checked={hideValidationGuide}
                  onChange={(e) => {
                    setHideValidationGuide(e.target.checked);
                    localStorage.setItem('hideReliefValidationGuide', e.target.checked);
                  }}
                  className="rounded border-gray-300"
                />
                Don't show this again
              </label>
              <button
                type="button"
                onClick={() => setShowValidationGuide(false)}
                className="px-4 py-2 bg-sky-600 text-white rounded-md text-sm hover:bg-sky-700"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 md:rounded-lg relative">
        <table className="min-w-full divide-y divide-gray-300" style={{ tableLayout: "fixed" }}>
          <thead className="bg-gray-50">
            <tr>
              <ResizableHeader col="type" label="Type" />
              <ResizableHeader col="period" label="Period" />
              <ResizableHeader col="description" label="Description" />
              <ResizableHeader col="date" label="Date" />
              <ResizableHeader col="gross" label="Gross/Base" align="right" />
              <ResizableHeader col="tax" label="VAT / Withheld" align="right" />
              <ResizableHeader col="net" label="Net" align="right" />
              {showActions && (
                <ResizableHeader col="actions" label="Actions" align="right" isSticky />
              )}
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-200">
            {currentRecords.map((record) => {
              const kind = getRecordKind(record);
              const vals = getDisplayValues(record);
              const quarterDisplay = getQuarterDisplay(record);
              const isEditing = editingId === record._id;
              const payee = isCertificateKind(kind) ? getForm2306Payee(record) : null;

              return (
                <tr key={record._id} className={isEditing ? "bg-primary-50" : "hover:bg-ink-canvas"}>
                  {/* Type — record kind is a category fact, not a status: uniform tint, text differentiates */}
                  <td style={{ width: colWidths.type, minWidth: 80 }} className="px-6 py-4 whitespace-nowrap">
                    {isCertificateKind(kind) ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-primary-50 text-primary-700">
                        {kind === "form2307" ? "Form 2307" : "Form 2306"}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-primary-50 text-primary-700">
                        {record.recordType === "input" ? "Input" : "Output"}
                      </span>
                    )}
                  </td>

                  {/* Period */}
                  <td style={{ width: colWidths.period, minWidth: 80 }} className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{quarterDisplay.fullLabel}</div>
                    <div className="text-xs text-gray-500">{quarterDisplay.label}</div>
                    {isCertificateKind(kind) && Number(record?.month) ? (
                      <div className="text-xs text-amber-700 mt-1">
                        {MONTH_OPTIONS.find((m) => m.value === Number(record.month))?.label || "—"} {quarterDisplay.fiscalYear}
                      </div>
                    ) : null}
                  </td>

                  {/* Desc */}
                  <td style={{ width: colWidths.description, minWidth: 80 }} className="px-6 py-4">
                    {kind === "vat" && isEditing ? (
                      <div className="space-y-3">
                        <ChartOfAccountsSearch
                          value={(editingData?.chartOfAccountName || editingData?.description) ?? ""}
                          onChange={(value) => handleEditChange("description", value)}
                          onSelect={handleChartOfAccountSelect}
                          placeholder="Search chart of accounts or enter description"
                          showLinkedBadge={false}
                        />
                        
                        {/* Vendor Fields (for input records) */}
                        {editingData?.recordType === "input" && (
                          <div className="space-y-2 border-t pt-2">
                            <input
                              type="text"
                              value={editingData?.vendorName ?? ""}
                              onChange={(e) => handleEditChange("vendorName", e.target.value)}
                              placeholder={`${term.one} Name`}
                              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                            />
                            <input
                              type="text"
                              value={editingData?.vendorTin ?? ""}
                              onChange={(e) => handleEditChange("vendorTin", e.target.value)}
                              placeholder={`${term.one} TIN`}
                              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                            />
                            <select
                              value={editingData?.vendorClassification ?? ""}
                              onChange={(e) => handleEditChange("vendorClassification", e.target.value)}
                              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                            >
                              <option value="">— Classification —</option>
                              <option value="corporation">Corporation</option>
                              <option value="individual">Individual</option>
                            </select>
                            <input
                              type="text"
                              value={editingData?.supplierAddress ?? ""}
                              onChange={(e) => handleEditChange("supplierAddress", e.target.value)}
                              placeholder="Supplier Address"
                              className={`w-full border border-ink-line rounded px-2 py-1 text-sm ${
                                editingData?.supplierAddressLocked ? 'bg-ink-canvas text-ink-body cursor-not-allowed' : ''
                              }`}
                              readOnly={!!editingData?.supplierAddressLocked}
                            />
                            {editingData?.vendor && !editingData?.supplierAddressLocked && (
                              <p className="text-xs text-warning-500">
                                No address on file — you can enter one here, or update it permanently in Vendor Maintenance.
                              </p>
                            )}
                            <select
                              value={editingData?.purchaseType || 'goods_other'}
                              onChange={(e) => handleEditChange('purchaseType', e.target.value)}
                              className="w-full border border-gray-300 rounded px-2 py-1 text-sm mt-1"
                            >
                              <option value="goods_other">Goods Other than Capital Goods</option>
                              <option value="capital_goods">Capital Goods</option>
                              <option value="services">Services</option>
                            </select>
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={editingData?.isZeroRated ?? false}
                                onChange={(e) => handleVatZeroRatedToggle(e.target.checked)}
                                className="rounded border-gray-300"
                              />
                              <span>Zero-Rated Purchase</span>
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={editingData?.isVatExempt ?? false}
                                onChange={(e) => handleVatExemptToggle(e.target.checked)}
                                className="rounded border-gray-300"
                              />
                              <span>VAT Exempt</span>
                            </label>
                          </div>
                        )}
                        
                        {/* Customer Fields (for output records) */}
                        {editingData?.recordType === "output" && (
                          <div className="space-y-2 border-t pt-2">
                            <input
                              type="text"
                              value={editingData?.customerName ?? ""}
                              onChange={(e) => handleEditChange("customerName", e.target.value)}
                              placeholder="Customer Name"
                              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                            />
                            <input
                              type="text"
                              value={editingData?.customerTin ?? ""}
                              onChange={(e) => handleEditChange("customerTin", e.target.value)}
                              placeholder="Customer TIN"
                              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                            />
                            <select
                              value={editingData?.customerClassification ?? ""}
                              onChange={(e) => handleEditChange("customerClassification", e.target.value)}
                              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                            >
                              <option value="">— Classification —</option>
                              <option value="corporation">Corporation</option>
                              <option value="individual">Individual</option>
                            </select>
                            <input
                              type="text"
                              value={editingData?.customerAddress ?? ""}
                              onChange={(e) => handleEditChange("customerAddress", e.target.value)}
                              placeholder="Customer Address"
                              className={`w-full border border-ink-line rounded px-2 py-1 text-sm ${
                                editingData?.customerAddressLocked ? 'bg-ink-canvas text-ink-body cursor-not-allowed' : ''
                              }`}
                              readOnly={!!editingData?.customerAddressLocked}
                            />
                            {editingData?.customer && !editingData?.customerAddressLocked && (
                              <p className="text-xs text-warning-500">
                                No address on file — you can enter one here, or update it permanently in Customer Maintenance.
                              </p>
                            )}
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={editingData?.isService ?? false}
                                onChange={(e) => handleEditChange("isService", e.target.checked)}
                                className="rounded border-gray-300"
                              />
                              <span>Service Transaction</span>
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={editingData?.isZeroRated ?? false}
                                onChange={(e) => handleVatZeroRatedToggle(e.target.checked)}
                                className="rounded border-gray-300"
                              />
                              <span>Zero-Rated Sale</span>
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={editingData?.isVatExempt ?? false}
                                onChange={(e) => handleVatExemptToggle(e.target.checked)}
                                className="rounded border-gray-300"
                              />
                              <span>VAT Exempt</span>
                            </label>
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        <div className="text-sm text-gray-900">
                          {record.description?.trim()
                            || (kind === "form2306" ? "EWT - Form 2306" : kind === "form2307" ? "CWT - Form 2307" : "—")}
                        </div>
                        {record.chartOfAccountName && <div className="text-xs text-gray-500 mt-1">{record.chartOfAccountName}</div>}
                        {isCertificateKind(kind) && (
                          <div className="text-xs text-gray-600 mt-1">
                            Payee: <span className="font-medium">{payee?.name || "—"}</span>
                            {payee?.tin ? <span className="ml-2 text-gray-500">TIN: {payee.tin}</span> : null}
                          </div>
                        )}
                        {kind === "vat" && record.recordType === "input" && (
                          <>
                            <div className="text-xs text-ink-body mt-1">{record.vendorName || "—"}</div>
                            <div className="text-xs mt-0.5">
                              {record.vendorTin ? (
                                <span className="text-ink-body">TIN: {record.vendorTin}</span>
                              ) : (
                                <span className="text-warning-500">TIN missing</span>
                              )}
                            </div>
                            <div className="text-xs mt-0.5">
                              {record.supplierAddress ? (
                                <span className="text-ink-body">{record.supplierAddress}</span>
                              ) : (
                                <span className="text-warning-500">Address missing</span>
                              )}
                            </div>
                            <div className="mt-1 flex flex-wrap gap-1">
                              {record.vendorClassification === 'corporation' && (
                                <span className="text-xs bg-ink-canvas text-ink-body px-1.5 py-0.5 rounded-md">Corp</span>
                              )}
                              {record.vendorClassification === 'individual' && (
                                <span className="text-xs bg-ink-canvas text-ink-body px-1.5 py-0.5 rounded-md">Indiv</span>
                              )}
                              {record.purchaseType && PURCHASE_TYPE_LABELS[record.purchaseType] && (
                                <span className={`text-xs px-1.5 py-0.5 rounded-md ${PURCHASE_TYPE_LABELS[record.purchaseType].color}`}>
                                  {PURCHASE_TYPE_LABELS[record.purchaseType].label}
                                </span>
                              )}
                              {record.isZeroRated && (
                                <span className="text-xs bg-ink-canvas text-ink-body px-1.5 py-0.5 rounded-md">Zero-rated</span>
                              )}
                            </div>
                          </>
                        )}
                        {kind === "vat" && record.recordType === "output" && (
                          <>
                            <div className="text-xs text-ink-body mt-1">{record.customerName || "—"}</div>
                            <div className="text-xs mt-0.5">
                              {record.customerTin ? (
                                <span className="text-ink-body">TIN: {record.customerTin}</span>
                              ) : (
                                <span className="text-warning-500">TIN missing</span>
                              )}
                            </div>
                            <div className="text-xs mt-0.5">
                              {record.customerAddress ? (
                                <span className="text-ink-body">{record.customerAddress}</span>
                              ) : (
                                <span className="text-warning-500">Address missing</span>
                              )}
                            </div>
                            <div className="mt-1 flex flex-wrap gap-1">
                              {record.customerClassification === 'corporation' && (
                                <span className="text-xs bg-ink-canvas text-ink-body px-1.5 py-0.5 rounded-md">Corp</span>
                              )}
                              {record.customerClassification === 'individual' && (
                                <span className="text-xs bg-ink-canvas text-ink-body px-1.5 py-0.5 rounded-md">Indiv</span>
                              )}
                              {record.isService && (
                                <span className="text-xs bg-ink-canvas text-ink-body px-1.5 py-0.5 rounded-md">Service</span>
                              )}
                              {record.isZeroRated && (
                                <span className="text-xs bg-ink-canvas text-ink-body px-1.5 py-0.5 rounded-md">Zero-rated</span>
                              )}
                              {isVatExempt(record) && (
                                <span className="text-xs bg-ink-canvas text-ink-body px-1.5 py-0.5 rounded-md">VAT exempt</span>
                              )}
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </td>

                  {/* Date */}
                  <td style={{ width: colWidths.date, minWidth: 80 }} className="px-6 py-4 whitespace-nowrap">
                    {isEditing ? (
                      <input
                        type="date"
                        value={editingData?.transactionDate ?? ""}
                        onChange={(e) => handleEditChange("transactionDate", e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                    ) : (
                      <span className="text-sm text-gray-500">{formatDate(record.transactionDate)}</span>
                    )}
                  </td>

                  {/* Gross/Base */}
                  <td style={{ width: colWidths.gross, minWidth: 80 }} className="px-6 py-4 whitespace-nowrap text-right">
                    {isEditing && editingData?.kind === "vat" ? (
                      <>
                        <MoneyInput
                          value={editingData?.grossAmount ?? 0}
                          onValueChange={handleVatGrossAmountChange}
                          className="w-32"
                        />
                        {isEditing && editingData?.kind === "vat" && !editingData?.isVatExempt && (
                          <div className="text-xs text-ink-body mt-1 text-right">
                            VAT: {formatCurrency(editingData?.vatAmount)} | Net: {formatCurrency(editingData?.netAmount)}
                          </div>
                        )}
                      </>
                    ) : isEditing && editingData?.kind === "form2306" ? (
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editingData?.baseAmount ?? 0}
                        onChange={(e) => handle2306BaseAmountChange(e.target.value)}
                        className="w-32 border border-ink-line rounded px-2 py-1 text-sm text-right"
                      />
                    ) : (
                      <span className="text-sm text-ink">{formatCurrency(vals.grossAmount)}</span>
                    )}
                  </td>

                  {/* Tax */}
                  <td style={{ width: colWidths.tax, minWidth: 80 }} className="px-6 py-4 whitespace-nowrap text-right">
                    {isEditing && editingData?.kind === "vat" ? (
                      <input
                        type="text"
                        value={formatCurrency(editingData?.vatAmount)}
                        readOnly
                        className="w-32 border border-ink-line rounded px-2 py-1 text-sm text-right tabular-nums bg-ink-canvas text-ink-body cursor-not-allowed"
                      />
                    ) : isEditing && editingData?.kind === "form2306" ? (
                      <input
                        type="number"
                        value={editingData?.taxWithheld ?? 0}
                        readOnly
                        className="w-32 border border-ink-line rounded px-2 py-1 text-sm text-right bg-ink-canvas text-ink-body cursor-not-allowed"
                      />
                    ) : (
                      <span className="text-sm text-ink">{formatCurrency(vals.taxAmount)}</span>
                    )}
                  </td>

                  {/* Net */}
                  <td style={{ width: colWidths.net, minWidth: 80 }} className="px-6 py-4 whitespace-nowrap text-right">
                    {isEditing && editingData?.kind === "vat" ? (
                      <input
                        type="text"
                        value={formatCurrency(editingData?.netAmount)}
                        readOnly
                        className="w-32 border border-ink-line rounded px-2 py-1 text-sm text-right tabular-nums bg-ink-canvas text-ink-body cursor-not-allowed"
                      />
                    ) : (
                      <span className="text-sm text-ink">{formatCurrency(vals.netAmount)}</span>
                    )}
                  </td>

                  {/* Actions */}
                  {showActions && (
                    <td
                      style={{
                        width: colWidths.actions,
                        minWidth: 80,
                        backgroundColor: isEditing ? '#fefce8' : 'white',
                      }}
                      className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium sticky right-0 z-10 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.08)]"
                    >
                      {isEditing ? (
                        <div className="flex gap-2 justify-end">
                          <button onClick={handleSaveEdit} disabled={saveLoading} className="text-green-600 hover:text-green-900 disabled:opacity-50">
                            {saveLoading ? "Saving..." : "Save"}
                          </button>
                          <button onClick={handleCancelEdit} disabled={saveLoading} className="text-gray-600 hover:text-gray-900 disabled:opacity-50">
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-2 justify-end">
                          {getRecordKind(record) === "vat" && (
                            <button
                              type="button"
                              onClick={() => handleOpenGLAudit(record)}
                              disabled={saveLoading}
                              className="px-2 py-1 rounded text-indigo-600 hover:bg-indigo-50"
                              title="View GL audit trail"
                            >
                              GL
                            </button>
                          )}
                          <button onClick={() => handleEditClick(record)} disabled={saveLoading} className="px-2 py-1 rounded text-primary-500 hover:bg-primary-50 disabled:opacity-50" aria-label="Edit record">
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(record)}
                            disabled={deletingId === record._id || saveLoading}
                            className="px-2 py-1 rounded text-danger-500 hover:bg-danger-50 disabled:opacity-50"
                            aria-label="Delete record"
                          >
                            {deletingId === record._id ? "…" : <Trash2 className="w-4 h-4" />}
                          </button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        isOpen={!!pendingDelete}
        title="Delete this record?"
        message="This can't be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={!!pendingDelete && deletingId === pendingDelete._id}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      {auditOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">VAT GL Audit Trail</h3>
                <p className="text-xs text-gray-500">{auditRecord?.description || auditRecord?._id || "Record"}</p>
              </div>
              <button type="button" onClick={handleCloseGLAudit} className="text-gray-500 hover:text-gray-800">Close</button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[70vh] space-y-4">
              {auditLoading && <p className="text-sm text-gray-600">Loading GL audit...</p>}
              {!auditLoading && auditError && <p className="text-sm text-red-600">{auditError}</p>}

              {!auditLoading && !auditError && (
                <>
                  <div className="text-xs text-gray-600">
                    Reference: <span className="font-mono">{auditData?.referenceNumber || "-"}</span>
                  </div>

                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 uppercase">Journal Entries</h4>
                    {(auditData?.journalEntries || []).length === 0 ? (
                      <p className="text-sm text-gray-500 mt-2">No journal entries found.</p>
                    ) : (
                      <div className="space-y-2 mt-2">
                        {(auditData?.journalEntries || []).map((entry) => (
                          <div key={entry._id} className="border rounded p-3">
                            <div className="flex flex-wrap gap-3 text-xs text-gray-700">
                              <span className="font-semibold">{entry.entryNumber || "(No #)"}</span>
                              <span>{entry.description}</span>
                              <span>{formatDate(entry.entryDate)}</span>
                              <span className="uppercase text-indigo-700">{entry.kind || "other"}</span>
                              <span>Dr {formatCurrency(entry.totalDebit || 0)}</span>
                              <span>Cr {formatCurrency(entry.totalCredit || 0)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 uppercase">General Ledger Rows</h4>
                    <p className="text-xs text-gray-500 mt-1">Count: {(auditData?.glEntries || []).length}</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Totals */}
      <div className="bg-ink-canvas px-6 py-3 rounded-lg">
        <div className="flex justify-between items-center text-sm">
          <span className="text-ink-body">Showing {currentRecords.length} of {records.length} records</span>
          <div className="flex space-x-6">
            <span className="text-ink font-medium tabular-nums">Total Gross/Base: {formatCurrency(totals.gross)}</span>
            <span className="text-ink font-medium tabular-nums">Total VAT/Withheld: {formatCurrency(totals.tax)}</span>
            <span className="text-ink font-medium tabular-nums">Total Net: {formatCurrency(totals.net)}</span>
          </div>
        </div>
      </div>

      {currentRecords.length === 0 && records.length > 0 && (
        <div className="text-center py-8 bg-ink-canvas rounded-lg">
          <h3 className="mt-2 text-sm font-medium text-ink">No matching records found</h3>
          <Button onClick={clearAllFilters} variant="ghost" size="sm" className="mt-3">
            Clear all filters
          </Button>
        </div>
      )}
    </div>
  );
};

export default VATRecordsList;